import type { StonApiClient } from '@ston-fi/api';
import { DEX, pTON } from '@ston-fi/sdk';
import { Address, beginCell, type Cell } from '@ton/core';
import {
  SWAP_PRMD_MASTER,
  SWAP_STON_POOL,
  SWAP_STON_PTON,
  SWAP_STON_PTON_ROUTER_WALLET,
  SWAP_STON_PRMD_ROUTER_WALLET,
  SWAP_STON_ROUTER,
  SWAP_TON_ASSET,
} from './config';
import { applySlippage } from './swap';
import {
  normalizedTonAddress,
  type StonPoolInfo,
  validateDirectStonPool,
} from './stonSwap';

export type LiquiditySource = 'ton' | 'prmd';
export type StonLiquiditySimulation = Awaited<
  ReturnType<StonApiClient['simulateLiquidityProvision']>
>;
export type StonWalletPoolInfo = Awaited<
  ReturnType<StonApiClient['queryPools']>
>[number];
export type StonRouterInfo = Awaited<ReturnType<StonApiClient['getRouter']>>;

export interface LiquidityPoolSnapshot {
  reserveTon: bigint;
  reservePrmd: bigint;
  totalLpSupply: bigint;
  lpFeeBps: bigint;
  protocolFeeBps: bigint;
  apy30D: string | null;
}

export interface LiquidityPosition {
  lpUnits: bigint;
  tonUnits: bigint;
  prmdUnits: bigint;
  sharePercent: string;
}

export interface LiquidityProvisionQuote {
  tonUnits: bigint;
  prmdUnits: bigint;
  estimatedLpUnits: bigint;
  minLpUnits: bigint;
  pendingTonUnits: bigint;
  pendingPrmdUnits: bigint;
}

export interface LiquidityTransaction {
  to: string | { toString(): string };
  value: bigint;
  body?: unknown;
}

export interface PreparedLiquidityTransaction {
  to: Address;
  value: bigint;
  body: Cell;
}

export interface OnchainLiquidityPoolData {
  isLocked: boolean;
  routerAddress: { toString(): string };
  totalSupplyLP: bigint;
  reserve0: bigint;
  reserve1: bigint;
}

function bigintField(value: string, label: string): bigint {
  const units = BigInt(value);
  if (units < 0n) throw new Error(`${label} cannot be negative`);
  return units;
}

function sameAddress(left: string, right: string): boolean {
  return normalizedTonAddress(left) === normalizedTonAddress(right);
}

export function createLiquidityPoolSnapshot(
  pool: StonPoolInfo | StonWalletPoolInfo,
): LiquidityPoolSnapshot {
  validateDirectStonPool(pool);
  if (pool.deprecated) throw new Error('STON.fi pool is deprecated');

  const token0IsPrmd = sameAddress(pool.token0Address, SWAP_PRMD_MASTER);
  const reservePrmd = bigintField(
    token0IsPrmd ? pool.reserve0 : pool.reserve1,
    'PRMD reserve',
  );
  const reserveTon = bigintField(
    token0IsPrmd ? pool.reserve1 : pool.reserve0,
    'TON reserve',
  );
  const totalLpSupply = bigintField(pool.lpTotalSupply, 'LP supply');
  const lpFeeBps = bigintField(pool.lpFee, 'LP fee');
  const protocolFeeBps = bigintField(pool.protocolFee, 'Protocol fee');

  if (reserveTon <= 0n || reservePrmd <= 0n || totalLpSupply <= 0n) {
    throw new Error('STON.fi pool has no usable liquidity');
  }
  if (lpFeeBps + protocolFeeBps > 1_000n) {
    throw new Error('STON.fi pool fee failed validation');
  }

  return {
    reserveTon,
    reservePrmd,
    totalLpSupply,
    lpFeeBps,
    protocolFeeBps,
    apy30D: pool.apy30D ?? null,
  };
}

export function createLiquidityPosition(
  pool: LiquidityPoolSnapshot,
  lpUnits: bigint,
): LiquidityPosition {
  if (lpUnits < 0n || lpUnits > pool.totalLpSupply) {
    throw new Error('LP balance failed validation');
  }

  return {
    lpUnits,
    tonUnits: (pool.reserveTon * lpUnits) / pool.totalLpSupply,
    prmdUnits: (pool.reservePrmd * lpUnits) / pool.totalLpSupply,
    sharePercent: formatLiquidityShare(lpUnits, pool.totalLpSupply),
  };
}

export function createRemovalQuote(
  pool: LiquidityPoolSnapshot,
  lpUnits: bigint,
  availableLpUnits: bigint,
): LiquidityPosition {
  if (lpUnits <= 0n || lpUnits > availableLpUnits) {
    throw new Error('LP burn amount exceeds the available balance');
  }
  return createLiquidityPosition(pool, lpUnits);
}

export function validateLiquiditySlippage(slippagePercent: number): void {
  if (
    !Number.isFinite(slippagePercent) ||
    slippagePercent < 0.1 ||
    slippagePercent > 5
  ) {
    throw new Error('Liquidity slippage must be between 0.1 and 5 percent');
  }
}

export function validateLiquiditySimulation(
  simulation: StonLiquiditySimulation,
  source: LiquiditySource,
  sourceUnits: bigint,
  slippagePercent: number,
): LiquidityProvisionQuote {
  validateLiquiditySlippage(slippagePercent);
  if (sourceUnits <= 0n) throw new Error('Liquidity amount must be positive');
  if (simulation.provisionType !== 'Balanced') {
    throw new Error('STON.fi returned an unexpected liquidity provision type');
  }
  if (
    !sameAddress(simulation.poolAddress, SWAP_STON_POOL) ||
    !sameAddress(simulation.routerAddress, SWAP_STON_ROUTER) ||
    !sameAddress(simulation.router.address, SWAP_STON_ROUTER)
  ) {
    throw new Error('STON.fi liquidity route is not allowlisted');
  }
  validateLiquidityRouter(simulation.router);

  const tokenAIsTon = sameAddress(simulation.tokenA, SWAP_TON_ASSET);
  const tokenBIsPrmd = sameAddress(simulation.tokenB, SWAP_PRMD_MASTER);
  if (!tokenAIsTon || !tokenBIsPrmd) {
    throw new Error('STON.fi liquidity assets do not match TON/PRMD');
  }

  const tonUnits = bigintField(simulation.tokenAUnits, 'TON amount');
  const prmdUnits = bigintField(simulation.tokenBUnits, 'PRMD amount');
  const estimatedLpUnits = bigintField(
    simulation.estimatedLpUnits,
    'Estimated LP amount',
  );
  const minLpUnits = bigintField(simulation.minLpUnits, 'Minimum LP amount');
  const pendingTonUnits = bigintField(
    simulation.lpAccountTokenABalance,
    'Pending TON amount',
  );
  const pendingPrmdUnits = bigintField(
    simulation.lpAccountTokenBBalance,
    'Pending PRMD amount',
  );
  const estimatedTonUnits = bigintField(
    simulation.estimatedTokenAUnits,
    'Estimated TON amount',
  );
  const estimatedPrmdUnits = bigintField(
    simulation.estimatedTokenBUnits,
    'Estimated PRMD amount',
  );
  const minTonUnits = bigintField(
    simulation.minTokenAUnits,
    'Minimum TON amount',
  );
  const minPrmdUnits = bigintField(
    simulation.minTokenBUnits,
    'Minimum PRMD amount',
  );
  const simulationLpSupply = bigintField(
    simulation.lpTotalSupply,
    'Simulation LP supply',
  );

  if (
    tonUnits <= 0n ||
    prmdUnits <= 0n ||
    estimatedLpUnits <= 0n ||
    minLpUnits <= 0n ||
    minLpUnits > estimatedLpUnits ||
    estimatedTonUnits <= 0n ||
    estimatedPrmdUnits <= 0n ||
    minTonUnits <= 0n ||
    minPrmdUnits <= 0n ||
    minTonUnits > estimatedTonUnits ||
    minPrmdUnits > estimatedPrmdUnits ||
    simulationLpSupply <= 0n
  ) {
    throw new Error('STON.fi returned unusable liquidity amounts');
  }
  if (
    (source === 'ton' && tonUnits !== sourceUnits) ||
    (source === 'prmd' && prmdUnits !== sourceUnits)
  ) {
    throw new Error('STON.fi changed the requested liquidity amount');
  }

  const localMinimum = applySlippage(estimatedLpUnits, slippagePercent);
  if (minLpUnits < localMinimum) {
    throw new Error('STON.fi minimum LP output is below the selected slippage');
  }

  return {
    tonUnits,
    prmdUnits,
    estimatedLpUnits,
    minLpUnits,
    pendingTonUnits,
    pendingPrmdUnits,
  };
}

export function validateLiquidityRouter(router: StonRouterInfo): void {
  if (
    !sameAddress(router.address, SWAP_STON_ROUTER) ||
    router.majorVersion !== 2 ||
    router.minorVersion !== 2 ||
    router.routerType !== 'ConstantProduct' ||
    router.ptonVersion !== '2.1' ||
    !sameAddress(router.ptonMasterAddress, SWAP_STON_PTON) ||
    !sameAddress(router.ptonWalletAddress, SWAP_STON_PTON_ROUTER_WALLET)
  ) {
    throw new Error('STON.fi router metadata failed validation');
  }
}

function createJettonTransferBody(params: {
  queryId: bigint;
  amount: bigint;
  destination: string;
  responseDestination: string;
  forwardTonAmount: bigint;
  forwardPayload: Cell;
}): Cell {
  return beginCell()
    .storeUint(0x0f8a7ea5, 32)
    .storeUint(params.queryId, 64)
    .storeCoins(params.amount)
    .storeAddress(Address.parse(params.destination))
    .storeAddress(Address.parse(params.responseDestination))
    .storeBit(false)
    .storeCoins(params.forwardTonAmount)
    .storeBit(true)
    .storeRef(params.forwardPayload)
    .endCell();
}

export async function createAddLiquidityTransactions(params: {
  walletAddress: string;
  quote: LiquidityProvisionQuote;
  expectedPrmdWallet: string;
  deadline: number;
  queryId: bigint;
}): Promise<{
  tonTx: PreparedLiquidityTransaction;
  prmdTx: PreparedLiquidityTransaction;
}> {
  const walletAddress = Address.parse(params.walletAddress).toString();
  const expectedPrmdWallet = Address.parse(params.expectedPrmdWallet);
  const router = DEX.v2_2.Router.CPI.create(SWAP_STON_ROUTER);
  const proxyTon = pTON.v2_1.create(SWAP_STON_PTON);

  const tonForwardPayload = await router.createProvideLiquidityBody({
    routerWalletAddress: SWAP_STON_PRMD_ROUTER_WALLET,
    receiverAddress: walletAddress,
    minLpOut: params.quote.minLpUnits,
    refundAddress: walletAddress,
    excessesAddress: walletAddress,
    bothPositive: true,
    deadline: params.deadline,
  });
  const tonForwardGas = BigInt(router.gasConstants.provideLpTon.forwardGasAmount);
  const tonBody = await proxyTon.createTonTransferBody({
    queryId: params.queryId,
    tonAmount: params.quote.tonUnits,
    refundAddress: walletAddress,
    forwardPayload: tonForwardPayload,
  });

  const prmdForwardPayload = await router.createProvideLiquidityBody({
    routerWalletAddress: SWAP_STON_PTON_ROUTER_WALLET,
    receiverAddress: walletAddress,
    minLpOut: params.quote.minLpUnits,
    refundAddress: walletAddress,
    excessesAddress: walletAddress,
    bothPositive: true,
    deadline: params.deadline,
  });
  const prmdForwardGas = BigInt(
    router.gasConstants.provideLpJetton.forwardGasAmount,
  );

  return {
    tonTx: {
      to: Address.parse(SWAP_STON_PTON_ROUTER_WALLET),
      value:
        params.quote.tonUnits +
        tonForwardGas +
        BigInt(proxyTon.gasConstants.tonTransfer),
      body: tonBody,
    },
    prmdTx: {
      to: expectedPrmdWallet,
      value: BigInt(router.gasConstants.provideLpJetton.gasAmount),
      body: createJettonTransferBody({
        queryId: params.queryId,
        amount: params.quote.prmdUnits,
        destination: SWAP_STON_ROUTER,
        responseDestination: walletAddress,
        forwardTonAmount: prmdForwardGas,
        forwardPayload: prmdForwardPayload,
      }),
    },
  };
}

export async function createBurnLiquidityTransaction(params: {
  amount: bigint;
  lpWalletAddress: string;
  queryId: bigint;
}): Promise<PreparedLiquidityTransaction> {
  if (params.amount <= 0n) throw new Error('LP burn amount must be positive');
  const pool = DEX.v2_2.Pool.CPI.create(SWAP_STON_POOL);
  return {
    to: Address.parse(params.lpWalletAddress),
    value: BigInt(pool.gasConstants.burn),
    body: await pool.createBurnBody({
      amount: params.amount,
      queryId: params.queryId,
    }),
  };
}

export async function createRefundLiquidityTransaction(params: {
  lpAccountAddress: string;
  queryId: bigint;
}): Promise<PreparedLiquidityTransaction> {
  const lpAccount = DEX.v2_2.LpAccount.create(params.lpAccountAddress);
  return {
    to: lpAccount.address,
    value: BigInt(lpAccount.gasConstants.refund),
    body: await lpAccount.createRefundBody({ queryId: params.queryId }),
  };
}

export function validateOnchainLiquidityPool(
  data: OnchainLiquidityPoolData,
): void {
  if (data.isLocked) throw new Error('STON.fi pool is locked');
  if (!sameAddress(data.routerAddress.toString(), SWAP_STON_ROUTER)) {
    throw new Error('STON.fi on-chain pool router is not allowlisted');
  }
  if (
    data.totalSupplyLP <= 0n ||
    data.reserve0 <= 0n ||
    data.reserve1 <= 0n
  ) {
    throw new Error('STON.fi on-chain pool has no usable liquidity');
  }
}

export function validateAddLiquidityTransactions(
  tonTx: LiquidityTransaction,
  prmdTx: LiquidityTransaction,
  params: {
    tonUnits: bigint;
    expectedPrmdWallet: string;
  },
): void {
  if (!tonTx.body || !prmdTx.body) {
    throw new Error('STON.fi liquidity transaction payload is missing');
  }
  if (!sameAddress(tonTx.to.toString(), SWAP_STON_PTON_ROUTER_WALLET)) {
    throw new Error('STON.fi TON liquidity destination is not allowlisted');
  }
  if (!sameAddress(prmdTx.to.toString(), params.expectedPrmdWallet)) {
    throw new Error('STON.fi PRMD wallet destination failed validation');
  }
  if (
    tonTx.value <= params.tonUnits ||
    tonTx.value > params.tonUnits + 500_000_000n ||
    prmdTx.value <= 0n ||
    prmdTx.value > 500_000_000n
  ) {
    throw new Error('STON.fi liquidity transaction value failed validation');
  }
}

export function validateBurnLiquidityTransaction(
  tx: LiquidityTransaction,
  expectedLpWallet: string,
): void {
  if (!tx.body) throw new Error('STON.fi burn transaction payload is missing');
  if (!sameAddress(tx.to.toString(), expectedLpWallet)) {
    throw new Error('STON.fi LP wallet destination failed validation');
  }
  if (tx.value <= 0n || tx.value > 1_000_000_000n) {
    throw new Error('STON.fi burn transaction value failed validation');
  }
}

export function validateRefundLiquidityTransaction(
  tx: LiquidityTransaction,
  expectedLpAccount: string,
): void {
  if (!tx.body) throw new Error('STON.fi refund transaction payload is missing');
  if (!sameAddress(tx.to.toString(), expectedLpAccount)) {
    throw new Error('STON.fi LP account destination failed validation');
  }
  if (tx.value <= 0n || tx.value > 1_000_000_000n) {
    throw new Error('STON.fi refund transaction value failed validation');
  }
}

export function formatLiquidityShare(lpUnits: bigint, totalLpSupply: bigint): string {
  if (lpUnits <= 0n || totalLpSupply <= 0n) return '0';
  const thousandthsOfPercent = (lpUnits * 100_000n) / totalLpSupply;
  const whole = thousandthsOfPercent / 1_000n;
  const fraction = (thousandthsOfPercent % 1_000n)
    .toString()
    .padStart(3, '0')
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole.toString();
}

export function formatApproxLiquidityUnits(
  value: bigint | string,
  decimals = 9,
): string {
  const units = typeof value === 'bigint' ? value : BigInt(value);
  const base = 10n ** BigInt(decimals);
  const scales = [
    { threshold: 1_000_000_000n * base, divisor: 1_000_000_000n * base, suffix: 'B' },
    { threshold: 1_000_000n * base, divisor: 1_000_000n * base, suffix: 'M' },
    { threshold: 1_000n * base, divisor: 1_000n * base, suffix: 'K' },
  ];
  const compact = scales.find(({ threshold }) => units >= threshold);
  const divisor = compact?.divisor ?? base;
  const fractionDigits = compact ? 2 : 0;
  const precision = 10n ** BigInt(fractionDigits);
  const rounded = (units * precision + divisor / 2n) / divisor;
  if (units > 0n && rounded === 0n) return '~<1';
  const whole = rounded / precision;
  const fraction = (rounded % precision)
    .toString()
    .padStart(fractionDigits, '0')
    .replace(/0+$/, '');
  const amount = fraction ? `${whole}.${fraction}` : whole.toString();
  return `~${amount}${compact?.suffix ?? ''}`;
}

export function formatLiquidityUnits(
  value: bigint | string,
  maxFractionDigits = 4,
): string {
  const units = typeof value === 'bigint' ? value : BigInt(value);
  const base = 1_000_000_000n;
  const whole = units / base;
  const fraction = (units % base)
    .toString()
    .padStart(9, '0')
    .slice(0, Math.max(0, maxFractionDigits))
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole.toString();
}
